import React, { useEffect, useRef, useState } from "react";
import Email from "../../components/patient/Email/Email";
import dayjs, { Dayjs } from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import { resetAddIPDBillState } from "../../models/ipdBillsSlice";
import {
  resetHospitalToSchemeCodesMap,
  updateSelectedHospital,
} from "../../models/hospitalSlice";
import { errorMessage, successMessage } from "../../utils/notificationUtils";
import { Pagination } from "antd";
import { PaginationProps } from "antd";
import {
  downloadDocument,
  getAllMails,
  getEmailData,
  getVisitEmail,
  updateEmailData,
} from "../../apis/patientService";
import PatientListPreviewDocuments from "../../components/modals/PatientListPreviewDocuments";
import { getAllSchemes } from "../../models/patientSlice";

const containerStyle: React.CSSProperties = {
  background: "#f4f6fb",
  padding: "10px 14px",
  minHeight: "85vh",
  borderRadius: "16px",
  boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
  display: "flex",
  flexDirection: "column",
};

const EmailContainer = () => {
  // const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
  //   dayjs(dayjs().subtract(1, "week").format("DD-MM-YYYY"), "DD-MM-YYYY"),
  //   dayjs(dayjs().format("DD-MM-YYYY"), "DD-MM-YYYY"),
  // ]);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(
    null,
  );
  const { schemes } = useSelector((state: RootState) => state.patients);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalData, setTotalData] = useState<any>(null);
  const emptyDate = dayjs("");
  const [searchTerm, setSearchTerm] = useState<string | null>(null);
  const [previewDocument, setPreviewDocument] = useState(false);
  const [documentUrl, setDocumentUrl] = useState<string>("");
  const [clickedActiveTab, setClickedActiveTab] = useState<any>("");
  const [allEmails, setAllEmails] = useState<any>([]);
  const [emailsLoading, setEmailsLoading] = useState<boolean>(false);
  const [secondarySchemeSelections, setSecondarySchemeSelections] =
    useState<any>([]);
  const allowedSchemeNames = [
    "CGHS",
    "CAPF",
    "ECHS",
    "Ayushman Bharat",
    "CGHS NEW",
    "CAPF NEW",
  ];
  const secondarySchemes = Object.values(schemes)?.filter(
    (scheme) => !allowedSchemeNames.includes(scheme.schemeName),
  );
  const [isDownloadingDocument, setIsDownloadingDocument] =
    useState<boolean>(false);
  const [downloadingDocumentUrl, setDownloadingDocumentUrl] =
    useState<string>("");

  const { hospital, auth } = useSelector((state: RootState) => state);
  const dispatch = useDispatch<AppDispatch>();
  const currentSelectedFilterTypeRef = useRef<any>(null);
  const [sorting, setSorting] = useState<any>({});

  // Sort field mapper - maps table column field names to API field names
  const SORT_FIELD_MAP: { [key: string]: string } = {
    doa: "DOA",
    healthCareName: "HOSPITAL_NAME",
    updatedAt: "UPDATED_AT",
    updatedStatus: "STATUS",
  };

  enum FilterType {
    ALL = "ALL",
    PREAUTH = "PREAUTH",
    DISCHARGE = "DISCHARGE",
    CLAIM = "CLAIM",
    STACK_VIEW = "STACK_VIEW",
  }

  const handleHospitalChange = async (hospitalId: number) => {
    if (hospital.selectedHospital?.value !== hospitalId) {
      dispatch(updateSelectedHospital(hospitalId));
      dispatch(resetAddIPDBillState());
      dispatch(resetHospitalToSchemeCodesMap());
      successMessage("HOSPITAL UPDATE");
      setCurrentPage(1);
      const filters = {
        hospitalId: hospitalId,
        schemeId: secondarySchemeSelections || null,
        startDate: dateRange?.[0] ? dateRange?.[0]?.format("DD-MM-YYYY") : null,
        endDate: dateRange?.[1] ? dateRange?.[1]?.format("DD-MM-YYYY") : null,
        search: searchTerm,
        pageSize: pageSize,
        pageNumber: 0,
        sortBy: sorting?.sortBy || "",
        sortDir: sorting?.sortDir || "",
      };
      await fetchEmails(filters);
    }
  };

  // useEffect(() => {
  //   if (auth?.serverTime) {
  //     const currDate = dayjs(auth?.serverTime).format("DD-MM-YYYY");
  //     const startDate = dayjs(auth.serverTime)
  //       .subtract(1, "week")
  //       .format("DD-MM-YYYY");

  //     setDateRange([
  //       dayjs(startDate, "DD-MM-YYYY"),
  //       dayjs(currDate, "DD-MM-YYYY"),
  //     ]);
  //   }
  // }, [auth.serverTime, hospital?.selectedHospital?.value]);

  const handleDateRangeChange = async (
    dates: [Dayjs | null, Dayjs | null] | null,
  ) => {
    setCurrentPage(1);
    if (dates) {
      setDateRange([
        dates?.[0] || dateRange?.[0] || emptyDate,
        dates?.[1] || dateRange?.[1] || emptyDate,
      ]);
      const filters = {
        hospitalId: hospital?.selectedHospital?.value,
        schemeId: secondarySchemeSelections || null,
        startDate: dates?.[0] ? dates?.[0]?.format("DD-MM-YYYY") : null,
        endDate: dates?.[1] ? dates?.[1]?.format("DD-MM-YYYY") : null,
        search: searchTerm,
        pageSize: pageSize,
        pageNumber: 0,
        sortBy: sorting?.sortBy || "",
        sortDir: sorting?.sortDir || "",
      };
      await fetchEmails(filters);
    } else {
      const filters = {
        hospitalId: hospital?.selectedHospital?.value,
        schemeId: secondarySchemeSelections || null,
        // startDate: dateRange?.[0] ? dateRange?.[0]?.format("DD-MM-YYYY") : null,
        // endDate: dateRange?.[1] ? dateRange?.[1]?.format("DD-MM-YYYY") : null,
        startDate: null,
        endDate: null,
        search: searchTerm,
        pageSize: pageSize,
        pageNumber: 0,
        sortBy: sorting?.sortBy || "",
        sortDir: sorting?.sortDir || "",
      };
      setDateRange(null);
      await fetchEmails(filters);
    }
  };

  const handleSecondarySchemeChange = async (values: number[]) => {
    setSecondarySchemeSelections(values);
    setCurrentPage(1);
    const filters = {
      hospitalId: hospital?.selectedHospital?.value,
      schemeId: values || [],
      startDate: dateRange?.[0] ? dateRange?.[0]?.format("DD-MM-YYYY") : null,
      endDate: dateRange?.[1] ? dateRange?.[1]?.format("DD-MM-YYYY") : null,
      search: searchTerm,
      pageSize: pageSize,
      pageNumber: 0,
      sortBy: sorting?.sortBy || "",
      sortDir: sorting?.sortDir || "",
    };
    await fetchEmails(filters);
  };

  const onPageChange: PaginationProps["onChange"] = async (
    page,
    pageSizeCurr,
  ) => {
    if (pageSizeCurr == pageSize) {
      setCurrentPage(page);

      if (hospital?.selectedHospital?.value) {
        const filters = {
          hospitalId: hospital?.selectedHospital?.value,
          schemeId: secondarySchemeSelections || null,
          startDate: dateRange?.[0]
            ? dateRange?.[0]?.format("DD-MM-YYYY")
            : null,
          endDate: dateRange?.[1] ? dateRange?.[1]?.format("DD-MM-YYYY") : null,
          search: searchTerm,
          pageSize: pageSize,
          pageNumber: page - 1,
          sortBy: sorting?.sortBy || "",
          sortDir: sorting?.sortDir || "",
        };
        await fetchEmails(filters);
      }
    }
  };

  const onShowSizeChange = async (current: number, newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);

    if (hospital?.selectedHospital?.value) {
      const filters = {
        hospitalId: hospital?.selectedHospital?.value,
        schemeId: secondarySchemeSelections || null,
        startDate: dateRange?.[0] ? dateRange?.[0]?.format("DD-MM-YYYY") : null,
        endDate: dateRange?.[1] ? dateRange?.[1]?.format("DD-MM-YYYY") : null,
        search: searchTerm,
        pageSize: newPageSize,
        pageNumber: 0,
        sortBy: sorting?.sortBy || "",
        sortDir: sorting?.sortDir || "",
      };
      await fetchEmails(filters);
    }
  };

  const handleOpen = (id: number, docType: any, docUrl: any) => {
    setPreviewDocument(true);
    setClickedActiveTab(docType);
    setDocumentUrl(docUrl);
  };

  const handleDownloadDocument = async (fileName: string, url: string) => {
    try {
      setDownloadingDocumentUrl(url);
      setIsDownloadingDocument(true);
      await downloadDocument(fileName, url);
      successMessage("Document Downloaded Successfully");
      setDownloadingDocumentUrl("");
      setIsDownloadingDocument(false);
    } catch (err: any) {
      errorMessage("Please, Try again later");
      setDownloadingDocumentUrl("");
      setIsDownloadingDocument(false);
    }
  };

  const fetchEmails = async (filters: any) => {
    try {
      setEmailsLoading(true);
       setAllEmails([]);
      setTotalData(0);
      if (
        currentSelectedFilterTypeRef.current &&
        currentSelectedFilterTypeRef.current !== FilterType.ALL &&
        currentSelectedFilterTypeRef.current !== FilterType.STACK_VIEW
      ) {
        filters.type = currentSelectedFilterTypeRef.current;
      }
      const res =
        currentSelectedFilterTypeRef.current === FilterType.STACK_VIEW
          ? await getVisitEmail(filters)
          : await getAllMails(filters);
      setEmailsLoading(false);
      setAllEmails(res?.content);
      setTotalData(res?.totalElements);
      if (currentSelectedFilterTypeRef.current === FilterType.STACK_VIEW) {
        setCurrentPage(res?.pageNumber + 1);
      } else {
        setCurrentPage(res?.number + 1);
      }
    } catch (err) {
      errorMessage("Please, Try again later");
      setEmailsLoading(false);
       setAllEmails([]);
      setTotalData(0);
    }
  };

  useEffect(() => {
    const filters = {
      hospitalId: hospital?.selectedHospital?.value,
      schemeId: secondarySchemeSelections || null,
      startDate: dateRange?.[0] ? dateRange?.[0]?.format("DD-MM-YYYY") : null,
      endDate: dateRange?.[1] ? dateRange?.[1]?.format("DD-MM-YYYY") : null,
      search: searchTerm,
      pageSize: pageSize,
      pageNumber: currentPage - 1,
      sortBy: sorting?.sortBy || "",
      sortDir: sorting?.sortDir || "",
    };

    fetchEmails(filters);
    dispatch(getAllSchemes());
  }, []);

  const handleSearch = async (isEmpty: boolean = false) => {
    setCurrentPage(1);
    if (isEmpty) {
      setSearchTerm(null);
      const filters = {
        hospitalId: hospital?.selectedHospital?.value,
        schemeId: secondarySchemeSelections || null,
        startDate: dateRange?.[0] ? dateRange?.[0]?.format("DD-MM-YYYY") : null,
        endDate: dateRange?.[1] ? dateRange?.[1]?.format("DD-MM-YYYY") : null,
        search: null,
        pageSize: pageSize,
        pageNumber: 0,
        sortBy: sorting?.sortBy || "",
        sortDir: sorting?.sortDir || "",
      };
      await fetchEmails(filters);
    } else {
      if (searchTerm) {
        setSearchTerm(searchTerm);
        const filters = {
          hospitalId: hospital?.selectedHospital?.value,
          schemeId: secondarySchemeSelections || null,
          startDate: dateRange?.[0]
            ? dateRange?.[0]?.format("DD-MM-YYYY")
            : null,
          endDate: dateRange?.[1] ? dateRange?.[1]?.format("DD-MM-YYYY") : null,
          search: searchTerm,
          pageSize: pageSize,
          pageNumber: 0,
          sortBy: sorting?.sortBy || "",
          sortDir: sorting?.sortDir || "",
        };
        await fetchEmails(filters);
      }
    }
  };

  const fetchEmailData = async (reqPayload: any) => {
    try {
      const res = await getEmailData(reqPayload);
      return res;
    } catch (err: any) {
      console.error("error in fetching email data", err);
      throw err;
    }
  };

  const handleUpdateEmailData = async (id: any, data: any) => {
    try {
      const res = await updateEmailData(id, data);
      successMessage("Email Data Updated Successfully");
      const filters = {
        hospitalId: hospital?.selectedHospital?.value,
        schemeId: secondarySchemeSelections || null,
        startDate: dateRange?.[0] ? dateRange?.[0]?.format("DD-MM-YYYY") : null,
        endDate: dateRange?.[1] ? dateRange?.[1]?.format("DD-MM-YYYY") : null,
        // startDate: null,
        // endDate: null,
        search: searchTerm,
        pageSize: pageSize,
        pageNumber: currentPage - 1,
        sortBy: sorting?.sortBy || "",
        sortDir: sorting?.sortDir || "",
      };

      fetchEmails(filters);
    } catch (err) {
      console.error("error in updating data", err);
      errorMessage("Please, Try again later");
    }
  };

  const handleFilterChange = async (type: any) => {
    try {
      setCurrentPage(1);
      setAllEmails([]); 
      setTotalData(0);
      const filters: any = {
        hospitalId: hospital?.selectedHospital?.value,
        schemeId: secondarySchemeSelections || null,
        startDate: dateRange?.[0] ? dateRange?.[0]?.format("DD-MM-YYYY") : null,
        endDate: dateRange?.[1] ? dateRange?.[1]?.format("DD-MM-YYYY") : null,
        search: searchTerm,
        pageSize: pageSize,
        pageNumber: 0,
        sortBy: sorting?.sortBy || "",
        sortDir: sorting?.sortDir || "",
      };

      currentSelectedFilterTypeRef.current = type;

      fetchEmails(filters);
    } catch (err) {
      console.error("error in updating data", err);
      errorMessage("Please, Try again later");
    }
  };

  const handleTableChange = async (
    _pagination: any,
    _filters: any,
    sorter: any,
  ) => {
    let sortDir = "";
    if (sorter.order === "ascend") {
      sortDir = "ASC";
    } else if (sorter.order === "descend") {
      sortDir = "DESC";
    }

    const sortBy = sortDir ? SORT_FIELD_MAP[sorter.field] : "";

    setSorting({ sortBy, sortDir });

    const filters = {
      hospitalId: hospital?.selectedHospital?.value,
      schemeId: secondarySchemeSelections || null,
      startDate: dateRange?.[0] ? dateRange?.[0]?.format("DD-MM-YYYY") : null,
      endDate: dateRange?.[1] ? dateRange?.[1]?.format("DD-MM-YYYY") : null,
      search: searchTerm,
      pageSize: pageSize,
      pageNumber: currentPage - 1,
      sortBy,
      sortDir,
    };

    await fetchEmails(filters);
  };

  return (
    <div style={containerStyle}>
      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          padding: "12px 8px",
          marginBottom: 24,
          minHeight: "60vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <h2
          style={{
            fontWeight: 700,
            fontSize: 20,
            color: "#273881",
            margin: 0,
            padding: "8px",
          }}
        >
          Emails
        </h2>
        <Email
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          dateRange={dateRange}
          handleDateRangeChange={handleDateRangeChange}
          availableHospitals={hospital.availableHospitals}
          selectedHospital={hospital.selectedHospital}
          handleHospitalChange={handleHospitalChange}
          handleOpen={handleOpen}
          downloadingDocumentUrl={downloadingDocumentUrl}
          isDownloadingDocument={isDownloadingDocument}
          handleDownloadDocument={handleDownloadDocument}
          allEmails={allEmails}
          handleSearch={handleSearch}
          handleSecondarySchemeChange={handleSecondarySchemeChange}
          secondarySchemeSelections={secondarySchemeSelections}
          secondarySchemes={secondarySchemes}
          emailsLoading={emailsLoading}
          handleUpdateEmailData={handleUpdateEmailData}
          fetchEmailData={fetchEmailData}
          currentPage={currentPage}
          pageSize={pageSize}
          handleFilterChange={handleFilterChange}
          handleTableChange={handleTableChange}
          sorting={sorting}
        />
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "flex-end",
            marginTop: 24,
          }}
        >
          <Pagination
            size="default"
            onChange={onPageChange}
            showSizeChanger
            style={{
              marginTop: "1em",
              background: "#f4f6fb",
              borderRadius: 8,
              padding: "4px 8px",
            }}
            current={currentPage}
            total={totalData}
            onShowSizeChange={onShowSizeChange}
            pageSizeOptions={["10", "20", "50", "100"]}
            defaultPageSize={100}
            pageSize={pageSize}
            showTotal={(total) => `Total ${total ?? 0} emails`}
          />
        </div>
      </div>
      <PatientListPreviewDocuments
        documentUrl={documentUrl}
        handleCancel={() => setPreviewDocument(false)}
        open={previewDocument}
        handleDownloadDocument={handleDownloadDocument}
        isDownloadingDocument={isDownloadingDocument}
        downloadingDocumentUrl={downloadingDocumentUrl}
        clickedActiveTab={clickedActiveTab}
      />
    </div>
  );
};

export default EmailContainer;
